import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { getResponsiveTextClasses, getResponsiveSpacingClasses, getFocusClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import type { ServiceCard } from '@/types';

interface ServicesCarouselProps {
  services: ServiceCard[];
  className?: string;
}

export const ServicesCarousel: React.FC<ServicesCarouselProps> = ({ 
  services, 
  className 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Configuración de cards por vista según el tamaño de pantalla
  const cardsPerView = {
    mobile: 1,
    tablet: 2,
    desktop: 4
  };

  // Navegación circular - avanza de 1 en 1
  const goToPrevious = () => {
    setCurrentIndex(prev => {
      if (prev === 0) return services.length - 1; // Loop to end
      return prev - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex(prev => {
      if (prev === services.length - 1) return 0; // Loop to start
      return prev + 1;
    });
  };

  // Calcular el ancho de cada card (25% para mostrar 4)
  const cardWidth = 100 / cardsPerView.desktop; // 25%
  
  // Duplicar servicios para crear efecto infinito
  const extendedServices = [...services, ...services, ...services];
  
  // Calcular el desplazamiento para centrar las 4 cards visibles
  // Empezamos desde el segundo set para permitir navegación en ambas direcciones
  const offset = services.length +.075;
  const translateX = -((currentIndex + offset) * cardWidth);

  return (
    <section className={cn(getResponsiveSpacingClasses('py', 8, 12, 16), 
    "bg-liec-neutral-50", 
    getTouchAccessibilityClasses(), 
    className )}
    style={{
      
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.8)), url(/fondos/02_Fondo.png)',
        backgroundSize: '300%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        
        
      }}
    >
      <div className="w-full ">
        {/* Section Header */}
        <div className={`text-center ${getResponsiveSpacingClasses('mb', 6, 8, 10)}`}>

                     <div className="inline-flex items-center gap-2 text-liec-primary-500 font-semibold text-sm uppercase tracking-wider mb-6">
            ¿QUÉ HACEMOS?
          </div>
           <svg className='inline-flex items-center gap-2 ml-2' width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<line x1="0.330774" y1="5.41623" x2="11.9289" y2="11.1156" stroke="#E34C0E" stroke-width="1.5"/>
<path d="M6.36253 13.292L12.4163 11.2088L10.5026 4.91892" stroke="#E34C0E" stroke-width="1.5"/>
</svg>

          <h2 className={getResponsiveTextClasses(
            'text-2xl font-bold text-liec-neutral-900 mb-4',
            'text-3xl',
            'text-4xl'
          )}>
            Servicios que importan
          </h2>
          <p className={`${getResponsiveTextClasses('text-base', 'text-lg', 'text-lg')} text-liec-neutral-600 max-w-3xl mx-auto`}>
            Más allá de los materiales, las pruebas que garantizan la calidad de tu obra y asegurando tu inversión.
          </p>
        </div>

        {/* Desktop Carousel (4 cards visible, slides 1 by 1) */}
        <div className="hidden lg:block relative">
          <div className="overflow-hidden">
            <div 
              className="flex gap-6 transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(${translateX}%)`,
              }}
            >
              {extendedServices.map((service, index) => (
                <div
                  key={`${service.id}-${index}`}
                  className="flex-shrink-0"
                  style={{ width: `calc(${cardWidth}% - 1.125rem)` }}
                >
                  <div
                    className={cn(
                      "group relative overflow-hidden  ",
                      "h-[620px]",
                      getFocusClasses(),
                      getTouchAccessibilityClasses()
                    )}
                  >
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-100"
                        loading="lazy"
                      />
                      {/* Dark Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
                    </div>

                    {/* Content */}
                    <div className="relative h-full flex flex-col justify-end p-8 text-white">
                      <h3 className="text-2xl font-bold mb-3 leading-tight">
                        {service.title}
                      </h3>
                      <p className="text-base leading-relaxed mb-5 line-clamp-3 opacity-90">
                        {service.description}
                      </p>
                      <Button
                        className={cn(
                          "bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold",
                          "shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105",
                          "w-full sm:w-auto",
                          getFocusClasses()
                        )}
                        asChild
                      >
                        <a href={service.link}>
                          CONOCE MÁS
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Always show */}
          {services.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 left-2 z-10",
                  "w-10 h-10 ",
                  "bg-white/20 text-white backdrop-blur-sm",
                  "text-white shadow-2xl",
                  "transition-all duration-300",
                  "hover:scale-110 hover:shadow-3xl",
                  "flex items-center justify-center",
                  getFocusClasses()
                )}
                aria-label="Servicios anteriores"
              >
                <ArrowLeft size={20} strokeWidth={2} />
              </button>

              <button
                onClick={goToNext}
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 right-2 z-10",
                  "w-10 h-10 ",
                  "bg-white/20 text-white backdrop-blur-sm",
                  "text-white shadow-2xl",
                  "transition-all duration-300",
                  "hover:scale-110 hover:shadow-3xl",
                  "flex items-center justify-center",
                  getFocusClasses()
                )}
                aria-label="Siguientes servicios"
              >
                <ArrowRight size={20} strokeWidth={2} />
              </button>
            </>
          )}

          {/* Dots Indicator - Show for each service 
          {services.length > 1 && (
            <div className={`flex justify-center ${getResponsiveSpacingClasses('mt', 6, 7, 12)} mt-6 gap-3`}>
              {services.map((_, index) => (
                <button
                  key={index}
                  className={cn(
                    "w-3 h-3 rounded-full transition-all duration-200",
                    getFocusClasses(),
                    index === currentIndex 
                      ? "bg-liec-primary-500 scale-110" 
                      : "bg-liec-neutral-300 hover:bg-liec-neutral-400"
                  )}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Ir al servicio ${index + 1}`}
                />
              ))}
            </div>
          )} */}
        </div>

        {/* Tablet Grid (2 cards) */}
        <div className="hidden md:grid lg:hidden grid-cols-2 gap-5">
          {services.map((service) => (
            <div
              key={service.id}
              className={cn(
                "group relative overflow-hidden  shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105",
                "h-96",
                getFocusClasses(),
                getTouchAccessibilityClasses()
              )}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
              </div>

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-7 text-white">
                <h3 className="text-2xl font-bold mb-3 leading-tight">
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed mb-5 line-clamp-3 opacity-90">
                  {service.description}
                </p>
                <Button
                  className={cn(
                    "bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold",
                    "transition-all duration-200 shadow-md hover:shadow-lg",
                    "w-full sm:w-auto",
                    getFocusClasses()
                  )}
                  asChild
                >
                  <a href={service.link}>
                    CONOCE MÁS
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Stack (1 card) */}
        <div className="md:hidden space-y-4">
          {services.map((service) => (
            <div
              key={service.id}
              className={cn(
                "group relative overflow-hidden  shadow-lg hover:shadow-2xl transition-all duration-300",
                "h-80",
                getFocusClasses(),
                getTouchAccessibilityClasses()
              )}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
              </div>

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-6 text-white">
                <h3 className="text-xl font-bold mb-3 leading-tight">
                  {service.title}
                </h3>
                <p className="text-sm leading-relaxed mb-5 line-clamp-3 opacity-90">
                  {service.description}
                </p>
                <Button
                  className={cn(
                    "bg-liec-primary-500 hover:bg-liec-primary-600 text-white font-semibold",
                    "transition-all duration-200 shadow-md hover:shadow-lg",
                    "w-full",
                    getFocusClasses()
                  )}
                  asChild
                >
                  <a href={service.link}>
                    CONOCE MÁS
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};